import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleFormField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import {
  cyclesApi,
  type CycleStatus,
  type ICycleAccessEntry,
  type IRankingCycle,
  type ISurveyPin,
  type PinAccess
} from '../api/cycles-api';
import { definitionsApi } from '../api/definitions-api';
import type { IDefinitionSurvey } from '../api/definitions-contracts';
import {
  AdminPage,
  LoadError,
  TableSkeleton,
  useAdminData
} from '../components/admin-page';
import { personHubPath } from '../components/participant-link';
import { cycleRemindersPath } from '../paths';
import { TEAMS_SURVEY } from '../lists';
import { refusalMessage } from '../refusal';

/**
 * Ranking cycles — the surface that decides which published version a respondent actually
 * sees. SR-123's last ticket, under `cycles.manage`.
 *
 * Publishing a definition does not put it in front of anybody. A respondent is served the
 * version their **cycle pins**, so a published version with no pin is a version nobody can
 * fill. Until these routes shipped that pin had to be written into the database by hand —
 * which is what README's local-setup section told people to do.
 *
 * ## The one write worth a warning
 *
 * Repointing a live cycle at a different version does **not** migrate the responses already
 * open against the old one, and does not close them: a response is frozen against the version
 * it opened with, by design. The server answers `retainedResponses` — a count, not a sentence
 * — and this screen shows that number after the write, because it is the only place the
 * consequence is visible.
 *
 * ## No freeze here
 *
 * A pin carried "Freeze figures…" until SR-451: an answer is now a metric the moment it is
 * submitted or corrected (survey-api's metrics journal), so there is nothing to copy, and a
 * ranking's cut-off is the ranking's own.
 *
 * ## One press past `cycles.manage`
 *
 * The section opens on `cycles.manage` (SR-228). "Pin a survey" offers the published versions,
 * read off the definitions under `definitions.author` — so without it the press is drawn off with
 * the reason and the versions are not read at all, rather than read into a 403.
 *
 * ## Who can start
 *
 * A pin admits anyone eligible, or only its access list (`config.access`, SR-487 ruling 17). The
 * pin's form sets it for a TEAMS pin alone (the plan's Q23), beside the version, sending the
 * config's other keys as read: only a team is put on an access list, so a `listed` pin of any
 * other survey would admit nobody. Another survey's form draws no such field and sends its config
 * as read. Each pin's row says its access as read, whatever the survey, and counts its list, read
 * under `responses.browse`, and opens it — each participant by name, who added them and when, and
 * Remove under `rankings.manage`. Without `responses.browse` the press is drawn off with the
 * reason and the list is not read; a failed read draws no count, and the list says why when
 * opened. Adding is a record's (its Team section) or Lists' selection's, where the participant is
 * in hand.
 */
export function AdminCyclesPage() {
  const [creating, setCreating] = React.useState(false);

  const { data: cycles, error, setError, reload } = useAdminData<IRankingCycle[]>((signal) =>
    cyclesApi.list(signal)
  );
  const canPin = useCan()('definitions.author');
  const { data: surveys } = useAdminData<IDefinitionSurvey[]>(
    (signal) => (canPin ? definitionsApi.surveys(signal) : Promise.resolve([])),
    [canPin]
  );

  return (
    <AdminPage title="Cycles">
      {error && <LoadError message={error} />}

      <Alert>
        <AlertTitle>A published version nobody pinned is a version nobody sees</AlertTitle>
        <AlertDescription>
          The respondent runtime serves whatever version the cycle pins, not the newest
          published one. This screen is where that choice is made.
        </AlertDescription>
      </Alert>

      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">{cycles?.length ?? 0} cycles</span>
        <Button className="ml-auto" onClick={() => setCreating(true)}>
          <Plus />
          New cycle
        </Button>
      </div>

      {creating && (
        <CreateCycle
          onCreated={() => {
            setCreating(false);
            void reload();
          }}
          onCancel={() => setCreating(false)}
          onFailed={setError}
        />
      )}

      {cycles === null ? (
        <TableSkeleton />
      ) : (
        cycles.map((cycle) => (
          <CycleCard
            key={cycle.code}
            cycle={cycle}
            surveys={surveys ?? []}
            canPin={canPin}
            onChanged={reload}
            onFailed={setError}
          />
        ))
      )}

      {cycles?.length === 0 && (
        <p className="text-sm text-muted-foreground">No ranking cycle exists yet.</p>
      )}
    </AdminPage>
  );
}

const STATUSES: CycleStatus[] = ['planned', 'open', 'closed', 'archived'];

const STATUS_VARIANT: Record<CycleStatus, 'success' | 'soft' | 'outline'> = {
  open: 'success',
  planned: 'soft',
  closed: 'outline',
  archived: 'outline'
};

function CreateCycle({
  onCreated,
  onCancel,
  onFailed
}: {
  onCreated: () => void;
  onCancel: () => void;
  onFailed: (message: string) => void;
}) {
  const [code, setCode] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      // `status` deliberately omitted: a create has no previous value to lose, so the server
      // defaults it to `planned` — which is also the only sane state for a cycle with no
      // surveys pinned into it yet.
      await cyclesApi.create({ code: code.trim(), name });
      onCreated();
    } catch (createError) {
      onFailed(refusalMessage(createError, 'Could not create this cycle'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New ranking cycle</CardTitle>
        <CardDescription>
          Starts as <code>planned</code> with nothing pinned into it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-wrap items-center gap-3" onSubmit={submit}>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="2028"
            aria-label="Cycle code"
            className="max-w-[10rem] font-mono"
          />
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="2028 ranking cycle"
            aria-label="Cycle name"
            className="max-w-sm"
          />
          <Button type="submit" disabled={busy || code.trim() === '' || name.trim() === ''}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function CycleCard({
  cycle,
  surveys,
  canPin,
  onChanged,
  onFailed
}: {
  cycle: IRankingCycle;
  surveys: IDefinitionSurvey[];
  /** `definitions.author` — the page's, which reads the versions a pin offers under it. */
  canPin: boolean;
  onChanged: () => void;
  onFailed: (message: string) => void;
}) {
  const [status, setStatus] = React.useState<CycleStatus>(cycle.status);
  const [name, setName] = React.useState(cycle.name);
  const [busy, setBusy] = React.useState(false);
  const [pinning, setPinning] = React.useState(false);
  const [retained, setRetained] = React.useState<Nullable<number>>(null);
  const pinReason = React.useId();

  React.useEffect(() => {
    setStatus(cycle.status);
    setName(cycle.name);
  }, [cycle.status, cycle.name]);

  const dirty = status !== cycle.status || name !== cycle.name;

  const saveCycle = async () => {
    setBusy(true);
    try {
      // Whole-block: both defaults are sent back as they were read, because omitting them
      // would clear the columns rather than leave them alone.
      await cyclesApi.update(cycle.code, {
        name,
        status,
        defaultOpensAt: cycle.defaultOpensAt,
        defaultClosesAt: cycle.defaultClosesAt
      });
      onChanged();
    } catch (saveError) {
      onFailed(refusalMessage(saveError, 'Could not save this cycle'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-mono text-base">
          {cycle.code}
          <Badge variant={STATUS_VARIANT[cycle.status]}>{cycle.status}</Badge>
        </CardTitle>
        <CardDescription>
          {cycle.name}
          {cycle.defaultOpensAt && ` · opens ${formatDateTime(cycle.defaultOpensAt)}`}
          {cycle.defaultClosesAt && ` · closes ${formatDateTime(cycle.defaultClosesAt)}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <SimpleFormField label="Name" htmlFor={`${cycle.code}-name`}>
            <Input
              id={`${cycle.code}-name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="max-w-sm"
            />
          </SimpleFormField>
          <SimpleFormField label="Status" htmlFor={`${cycle.code}-status`}>
            <Select value={status} onValueChange={(next) => setStatus(next as CycleStatus)}>
              <SelectTrigger id={`${cycle.code}-status`} className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {candidate}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SimpleFormField>
          <Button onClick={() => void saveCycle()} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save cycle'}
          </Button>
          <Button
            variant="outline"
            disabled={!canPin}
            aria-describedby={canPin ? undefined : pinReason}
            onClick={() => setPinning((open) => !open)}
          >
            <Plus />
            Pin a survey
          </Button>
        </div>
        {!canPin && <NotPermitted id={pinReason} what="pinning a survey version" />}

        {pinning && (
          <PinEditor
            cycleCode={cycle.code}
            surveys={surveys}
            existing={cycle.surveys}
            onDone={(count) => {
              setPinning(false);
              setRetained(count);
              onChanged();
            }}
            onFailed={onFailed}
          />
        )}

        {retained !== null && retained > 0 && (
          <Alert>
            <AlertTitle>{retained} response(s) stayed on the previous version</AlertTitle>
            <AlertDescription>
              A response is frozen against the version it opened with, so repointing a cycle
              does not move the ones already in flight. They were neither migrated nor closed.
            </AlertDescription>
          </Alert>
        )}

        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Survey</TableHead>
                <TableHead>Pinned version</TableHead>
                <TableHead>Pin status</TableHead>
                <TableHead>Who can start</TableHead>
                <TableHead>Opens</TableHead>
                <TableHead>Closes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cycle.surveys.map((pin) => (
                <PinRow key={pin.surveyCode} cycleCode={cycle.code} pin={pin} />
              ))}
              {cycle.surveys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                    Nothing pinned — no respondent can open a survey in this cycle.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/** "Who can start", in the words the form offers. */
const ACCESS_WORDS: Record<PinAccess, string> = {
  open: 'Anyone eligible',
  listed: 'Only the access list'
};

/** A pin's access as read (`pinAccessOf`), in those words — one the form does not offer, as
 *  spelled. */
export function accessWords(access: string): string {
  return ACCESS_WORDS[access as PinAccess] ?? access;
}

/** A pin's `config.access` — `open` where the config names none. */
export function pinAccessOf(config: unknown): string {
  const access =
    typeof config === 'object' && config !== null && 'access' in config
      ? (config as { access: unknown }).access
      : undefined;
  return typeof access === 'string' ? access : 'open';
}

/** The config a pin write sends: the one read, `access` set beside its other keys — or the one
 *  read untouched where it names no access and the choice is the default. */
function withPinAccess(config: unknown, access: PinAccess): unknown {
  const read =
    typeof config === 'object' && config !== null && !Array.isArray(config)
      ? (config as Record<string, unknown>)
      : null;
  if (access === 'open' && !(read && 'access' in read)) return config ?? null;
  return { ...read, access };
}

/** One pin of the cycle, and its access list opened under it. */
function PinRow({ cycleCode, pin }: { cycleCode: string; pin: ISurveyPin }) {
  const canRead = useCan()('responses.browse');
  const [open, setOpen] = React.useState(false);
  const list = useAdminData<ICycleAccessEntry[]>(
    (signal) =>
      canRead ? cyclesApi.access(cycleCode, pin.surveyCode, signal) : Promise.resolve([]),
    [cycleCode, pin.surveyCode, canRead]
  );
  const access = pinAccessOf(pin.config);
  const listId = React.useId();
  const reasonId = React.useId();
  // A count survey-api did not just say is not drawn: none unread, none after a failed read.
  const counted =
    !canRead || list.error !== null
      ? ''
      : ` (${list.data ? list.data.length.toLocaleString('en-US') : '…'})`;
  return (
    <>
      <TableRow data-pin={pin.surveyCode}>
        <TableCell className="font-medium">
          <span className="grid justify-items-start">
            {pin.surveyCode}
            <Link
              to={cycleRemindersPath(cycleCode, pin.surveyCode)}
              className="text-xs font-normal text-[var(--color-sapphire)] hover:underline"
              data-pin-reminders=""
            >
              Reminders ({pin.reminderSteps})
            </Link>
          </span>
        </TableCell>
        <TableCell>
          v{pin.versionNo}
          <span className="ml-2 text-xs text-muted-foreground">{pin.versionStatus}</span>
        </TableCell>
        <TableCell>
          <Badge variant={STATUS_VARIANT[pin.status]}>{pin.status}</Badge>
        </TableCell>
        <TableCell>
          <span className="grid justify-items-start gap-1" data-pin-access={access}>
            {accessWords(access)}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-auto px-0 text-[var(--color-sapphire)]"
              disabled={!canRead}
              aria-describedby={canRead ? undefined : reasonId}
              aria-expanded={open}
              aria-controls={open ? listId : undefined}
              onClick={() => setOpen((now) => !now)}
            >
              Access list{counted}
            </Button>
            {!canRead && <NotPermitted id={reasonId} what="reading who can start a survey" />}
          </span>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {pin.opensAt ? formatDateTime(pin.opensAt) : 'cycle default'}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {pin.closesAt ? formatDateTime(pin.closesAt) : 'cycle default'}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} id={listId}>
            <AccessList
              cycleCode={cycleCode}
              surveyCode={pin.surveyCode}
              entries={list.data}
              error={list.error}
              reload={list.reload}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * A pin's access list: each participant by name — a link to the record — who added them and
 * when, and Remove under `rankings.manage` (drawn off with the reason otherwise). A removal reads
 * the list again; a refusal is said under the list.
 */
function AccessList({
  cycleCode,
  surveyCode,
  entries,
  error,
  reload
}: {
  cycleCode: string;
  surveyCode: string;
  entries: Nullable<ICycleAccessEntry[]>;
  error: Nullable<string>;
  reload: () => Promise<boolean>;
}) {
  const permitted = useCan()('rankings.manage');
  const reasonId = React.useId();
  const [removing, setRemoving] = React.useState<Nullable<string>>(null);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);

  const remove = async (participantId: string) => {
    setRemoving(participantId);
    setRefusal(null);
    try {
      await cyclesApi.removeAccess(cycleCode, surveyCode, [participantId]);
      await reload();
    } catch (removeError) {
      setRefusal(refusalMessage(removeError, 'Could not remove them'));
    } finally {
      setRemoving(null);
    }
  };

  if (entries === null) {
    return error ? (
      <LoadError message={error} />
    ) : (
      <p className="text-sm text-muted-foreground">Reading the access list…</p>
    );
  }
  return (
    <div className="grid gap-2 text-sm">
      {error && <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />}
      {entries.length === 0 ? (
        <p className="text-muted-foreground">No one is on the access list.</p>
      ) : (
        <ul aria-label={`${surveyCode} access list`} className="grid gap-1.5">
          {entries.map((entry) => (
            <li
              key={entry.participantId}
              data-access-entry={entry.participantId}
              className="flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <Link
                to={personHubPath(entry.participantId)}
                className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
              >
                {entry.displayName}
              </Link>
              <span className="text-xs text-muted-foreground">
                Added by {entry.addedBy.email}, {formatDateTime(entry.addedAt)}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!permitted || removing !== null}
                aria-describedby={permitted ? undefined : reasonId}
                onClick={() => void remove(entry.participantId)}
              >
                {removing === entry.participantId ? 'Removing…' : 'Remove'}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {!permitted && entries.length > 0 && (
        <NotPermitted id={reasonId} what="changing who can start a survey" />
      )}
      {refusal && (
        <p role="alert" className="text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}

/**
 * Pins a survey, or repoints one already pinned.
 *
 * One form for both, because the server has one body for both: `POST …/surveys` adds and
 * `PUT …/surveys/{code}` replaces, and they take the same shape. Which one to call is decided
 * by whether the chosen survey is already in this cycle.
 *
 * Only **published** versions are offered. A draft cannot be pinned — the server refuses it —
 * and offering one would be an error the author only learns on submit.
 *
 * "Who can start" is offered for TEAMS alone (the page's "Who can start").
 */
function PinEditor({
  cycleCode,
  surveys,
  existing,
  onDone,
  onFailed
}: {
  cycleCode: string;
  surveys: IDefinitionSurvey[];
  existing: ISurveyPin[];
  onDone: (retained: number) => void;
  onFailed: (message: string) => void;
}) {
  const [surveyCode, setSurveyCode] = React.useState('');
  const [versionNo, setVersionNo] = React.useState('');
  const [status, setStatus] = React.useState<CycleStatus>('planned');
  const [access, setAccess] = React.useState<PinAccess>('open');
  const [busy, setBusy] = React.useState(false);

  const survey = surveys.find((candidate) => candidate.code === surveyCode);
  const publishable = (survey?.versions ?? [])
    .filter((version) => version.status === 'published')
    .sort((left, right) => right.versionNo - left.versionNo);
  const already = existing.find((pin) => pin.surveyCode === surveyCode);
  const teams = surveyCode === TEAMS_SURVEY;

  const submit = async () => {
    setBusy(true);
    const body = {
      survey: surveyCode,
      versionNo: Number(versionNo),
      status,
      opensAt: already?.opensAt ?? null,
      closesAt: already?.closesAt ?? null,
      // The config's other keys as read: the pin's `PUT` replaces the whole of it. Another
      // survey's is sent as read, `access` and all.
      config: teams ? withPinAccess(already?.config ?? null, access) : (already?.config ?? null)
    };
    try {
      const result = already
        ? await cyclesApi.updatePin(cycleCode, surveyCode, body)
        : await cyclesApi.addPin(cycleCode, body);
      onDone(result.retainedResponses);
    } catch (pinError) {
      onFailed(refusalMessage(pinError, 'Could not pin this survey'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-3 rounded-md border border-border px-4 py-3">
      <div className="flex flex-wrap items-end gap-3">
        <SimpleFormField label="Survey" htmlFor={`${cycleCode}-pin-survey`}>
          <Select
            value={surveyCode}
            onValueChange={(next) => {
              setSurveyCode(next);
              setVersionNo('');
              const pinned = existing.find((pin) => pin.surveyCode === next);
              setAccess(pinAccessOf(pinned?.config) === 'listed' ? 'listed' : 'open');
            }}
          >
            <SelectTrigger id={`${cycleCode}-pin-survey`} className="w-56">
              <SelectValue placeholder="Pick a survey" />
            </SelectTrigger>
            <SelectContent>
              {surveys.map((candidate) => (
                <SelectItem key={candidate.code} value={candidate.code}>
                  {candidate.code}
                  {existing.some((pin) => pin.surveyCode === candidate.code)
                    ? ' · already pinned'
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Version" htmlFor={`${cycleCode}-pin-version`}>
          <Select value={versionNo} onValueChange={setVersionNo}>
            <SelectTrigger id={`${cycleCode}-pin-version`} className="w-40">
              <SelectValue placeholder="Published only" />
            </SelectTrigger>
            <SelectContent>
              {publishable.map((version) => (
                <SelectItem key={version.surveyVersionId} value={String(version.versionNo)}>
                  v{version.versionNo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        <SimpleFormField label="Pin status" htmlFor={`${cycleCode}-pin-status`}>
          <Select value={status} onValueChange={(next) => setStatus(next as CycleStatus)}>
            <SelectTrigger id={`${cycleCode}-pin-status`} className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {candidate}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SimpleFormField>

        {teams && (
          <SimpleFormField label="Who can start" htmlFor={`${cycleCode}-pin-access`}>
            <Select value={access} onValueChange={(next) => setAccess(next as PinAccess)}>
              <SelectTrigger id={`${cycleCode}-pin-access`} className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['open', 'listed'] as const).map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {ACCESS_WORDS[candidate]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SimpleFormField>
        )}

        <Button onClick={() => void submit()} disabled={busy || !surveyCode || !versionNo}>
          {busy ? 'Pinning…' : already ? 'Repoint' : 'Pin'}
        </Button>
      </div>

      {survey && publishable.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {survey.code} has no published version, so there is nothing to pin. Publish one on
          the Versions tab first.
        </p>
      )}

      {already && (
        <p className="text-sm text-muted-foreground">
          {surveyCode} is pinned at v{already.versionNo}. Repointing leaves every response
          already open against it exactly where it is.
        </p>
      )}
    </div>
  );
}
